/* Draws the ring on the member home page ("Your health overview", snippets/pp-member-donut.liquid).
 *
 * THE LAYOUT IS COPIED FROM David's reference (2026-10-17: a ring with "106 Biomarkers" in the middle), measured on a 4x enlargement and
 * written down here in units of the ring's outer radius R, with the centre of the ring as (0, 0), x to the right, y down:
 *
 *   ring     stroke 0.114 R (here a little thinner, STROKE_R), round caps, three arcs with a gap of about 7 degrees between them
 *   arcs     (degrees clockwise from the top) orange starts at 88.6 and is the one that sits right of the ring; the third follows it;
 *            the big one fills the rest, so it runs from the bottom (about 177) round the left and over the top to about 82
 *   slot A   the big arc's number: its left edge at x -1.27, its centre at y -1.22 (top left, above the ring); label under it, same left edge.
 *            Leader: from just right of the number, level, to x -0.66, then straight down to the ring at 334 degrees.
 *   slot B   the orange arc's number: left edge x +1.18, centre y +0.33 (right of the ring). Leader: level, from the ring (at 108.7 degrees,
 *            which is that height) to just left of the number.
 *   slot C   the third arc's number: left edge x +0.80, centre y +1.03 in the reference, +1.20 here (David: lower, so the slant of the leader
 *            is easier to see). Leader: from the ring at 152 degrees, down and right to x +0.61, then level to just left of the number.
 *   middle   the number, in a thin serif, centre y -0.06; the word under it, centre y +0.40.
 *
 * So for data like the reference the picture is the reference. The arcs follow the counts inside fixed limits (the two small ones between 28
 * and 64 degrees, and 24 and 50, as a share of the reviewed count; a count of 0 is a dot), which is what keeps the leaders where the reference
 * has them. Where an arc is not at its slot's usual place the leader still starts on that arc and ends at the same fixed number.
 *
 * Drawn as SVG paths (arcs, not dashed circles) with whole-pixel sizes and no transforms, so the edges are anti-aliased and not jagged.
 * Without this script the card shows the middle text and the labels as a list.
 */
(function () {
  'use strict';

  var NS = 'http://www.w3.org/2000/svg';

  var STROKE_R = 0.105;      // ring stroke as a share of R (the reference's is 0.114; 0.092 was too thin, round 3: "a tiny bit thicker")
  var GAP_DEG = 3.6;         // visible gap between two arcs, cap to cap (the reference's is about 7.3; David, round 3: "too big", so about half)
  var ORANGE_START = 88.6;   // where the orange arc visibly starts
  var A_ATTACH = 334;        // where slot A's leader meets the ring
  var B_ATTACH = 108.7;
  var C_ATTACH = 152;

  var SLOT = {
    a: { left: -1.27, y: -1.22 },
    b: { left: 1.18, y: 0.33 },
    c: { left: 0.80, y: 1.20 }   // the reference has y 1.03; David asked for it lower so the slanted part of its leader shows
  };

  function rad(d) { return d * Math.PI / 180; }
  function deg(r) { return r * 180 / Math.PI; }
  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
  function mod(a) { return ((a % 360) + 360) % 360; }

  function el(name, attrs) {
    var e = document.createElementNS(NS, name);
    for (var k in attrs) if (Object.prototype.hasOwnProperty.call(attrs, k)) e.setAttribute(k, attrs[k]);
    return e;
  }

  function layout(root) {
    var stage = root.querySelector('.pp-donut__stage');
    var svg = root.querySelector('.pp-donut__svg');
    var center = root.querySelector('.pp-donut__center');
    var cnum = root.querySelector('.pp-donut__center-num');
    var clab = root.querySelector('.pp-donut__center-label');
    var call = {};
    [].forEach.call(root.querySelectorAll('.pp-donut__callout'), function (c) { call[c.getAttribute('data-slot')] = c; });
    if (!stage || !svg || !cnum || !clab || !call.a || !call.b || !call.c) return;

    var W = Math.floor(stage.clientWidth);
    if (!W) return;

    var total = Math.max(1, parseFloat(root.getAttribute('data-total')) || 0);
    var pr = parseFloat(root.getAttribute('data-priority')) || 0;
    var wa = parseFloat(root.getAttribute('data-watch')) || 0;

    // 1. the size of the ring: the biggest R (up to 112px) at which the whole picture still fits the card's width.
    root.classList.add('is-measuring');
    var R = 112;
    var wB = 0, wC = 0;
    for (var tries = 0; tries < 40; tries++) {
      root.style.setProperty('--pp-r', R + 'px');
      wB = call.b.offsetWidth;
      wC = call.c.offsetWidth;
      var need = 1.27 * R + Math.max(R, 1.18 * R + wB, 0.80 * R + wC);
      if (need <= W - 12 || R <= 64) break;   // 12px of air, so the picture never touches the card's padding
      R -= 2;
    }
    root.classList.remove('is-measuring');
    root.classList.add('is-laid-out');
    root.style.setProperty('--pp-r', R + 'px');

    var need2 = 1.27 * R + Math.max(R, 1.18 * R + call.b.offsetWidth, 0.80 * R + call.c.offsetWidth);
    var cx = Math.round((W - need2) / 2 + 1.27 * R);

    // 2. the height: from the top of slot A's number to the bottom of slot C's label, with a little air
    var aTop = 1.22 * R + call.a.querySelector('.pp-donut__callout-num').offsetHeight / 2;
    var cBottom = SLOT.c.y * R + call.c.querySelector('.pp-donut__callout-num').offsetHeight / 2 + 4 + call.c.querySelector('.pp-donut__callout-label').offsetHeight;
    var padV = 10;
    var cy = Math.round(padV + aTop);
    var H = Math.round(cy + Math.max(R, cBottom) + padV);
    stage.style.height = H + 'px';
    svg.setAttribute('width', W);
    svg.setAttribute('height', H);
    svg.setAttribute('viewBox', '0 0 ' + W + ' ' + H);
    while (svg.firstChild) svg.removeChild(svg.firstChild);

    // 3. the arcs
    var strokeW = Math.max(6, Math.round(R * STROKE_R * 2) / 2);
    var rMid = R - strokeW / 2;
    var capDeg = deg((strokeW / 2) / rMid);
    var dotVis = 2 * capDeg;                        // what a dot (a count of 0) takes up, cap to cap

    var shareP = clamp(pr / total, 0, 1);
    var shareW = clamp(wa / total, 0, 1);
    var orangeVis = pr > 0 ? 28 + 36 * shareP : dotVis;
    var thirdVis = wa > 0 ? 24 + 26 * shareW : dotVis;
    // The orange arc starts where the reference's does. A count of 0 is a dot, and a dot sits exactly where its leader meets the ring in the
    // reference (108.7 and 152 degrees), so two zeros do not crowd each other and no leader has to cross the ring. The third arc keeps its
    // place near 152 unless the orange one is long enough to push it on.
    var o0 = pr > 0 ? ORANGE_START : B_ATTACH - orangeVis / 2, o1 = o0 + orangeVis;
    var t0 = Math.max(o1 + GAP_DEG, C_ATTACH - thirdVis / 2), t1 = t0 + thirdVis;
    var g0 = t1 + GAP_DEG, g1 = o0 + 360 - GAP_DEG;

    function pt(a, r) { return { x: cx + r * Math.sin(rad(a)), y: cy - r * Math.cos(rad(a)) }; }
    function arc(v0, v1, cls) {
      var a0 = v0 + capDeg, a1 = v1 - capDeg;       // the path stops a cap short at each end; the round caps give it back
      if (a1 < a0) a1 = a0 + 0.05;                  // a dot: a path of no length still draws its two round caps
      var p0 = pt(a0, rMid), p1 = pt(a1, rMid);
      var large = (a1 - a0) > 180 ? 1 : 0;
      svg.appendChild(el('path', {
        'class': 'pp-donut__arc ' + cls,
        d: 'M' + p0.x.toFixed(2) + ' ' + p0.y.toFixed(2) + ' A' + rMid.toFixed(2) + ' ' + rMid.toFixed(2) + ' 0 ' + large + ' 1 ' + p1.x.toFixed(2) + ' ' + p1.y.toFixed(2),
        'stroke-width': strokeW
      }));
    }
    arc(g0, g1, 'pp-donut__arc--total');
    arc(o0, o1, 'pp-donut__arc--priority');
    arc(t0, t1, 'pp-donut__arc--watch');

    // 4. the numbers, each at its slot, and the leaders
    function place(slot, key) {
      var c = call[slot], s = SLOT[slot];
      var n = c.querySelector('.pp-donut__callout-num');
      var left = Math.round(cx + s.left * R);
      var top = Math.round(cy + s.y * R - n.offsetHeight / 2);
      c.style.left = left + 'px';
      c.style.top = top + 'px';
      return { left: left, ny: cy + s.y * R, numW: n.offsetWidth };
    }
    var A = place('a'), B = place('b'), C = place('c');

    function leader(points) {
      var d = '';
      points.forEach(function (p, i) { d += (i ? 'L' : 'M') + p.x.toFixed(1) + ' ' + p.y.toFixed(1) + ' '; });
      svg.appendChild(el('path', { 'class': 'pp-donut__lead', d: d.trim() }));
    }
    var out = R + 3;

    // slot A: level from the number, then down to the ring
    var aY = cy - 1.25 * R;
    var q = { x: A.left + A.numW + 0.09 * R, y: aY };
    var bend = { x: Math.max(cx - 0.66 * R, q.x + 0.12 * R), y: aY };
    // The reference's label under this number is short ("In Range"); a longer one (this card's "Health areas reviewed") would run into the
    // slanted part of the leader. So the leader is moved along the ring, clockwise, as far as it takes to clear the label's text by a few px.
    var range = document.createRange();
    range.selectNodeContents(call.a.querySelector('.pp-donut__callout-label'));
    var tr = range.getBoundingClientRect(), sr = stage.getBoundingClientRect();
    var labTop = tr.top - sr.top, labRight = tr.right - sr.left;
    var aAng = A_ATTACH;
    for (var ang = A_ATTACH; ang <= A_ATTACH + 24; ang += 1) {
      var pa = pt(ang, out);
      var xAtLabelTop = bend.x + (labTop - bend.y) * (pa.x - bend.x) / (pa.y - bend.y);
      aAng = ang;
      if (xAtLabelTop >= labRight + 6) break;
    }
    leader([q, bend, pt(aAng, out)]);

    // slot B: from the ring (as near its own height as the arc allows), level into the number
    var bAng = pr > 0 ? clamp(B_ATTACH, o0 + 4, Math.max(o0 + 4, o1 - 4)) : (o0 + o1) / 2;
    leader([pt(bAng, out), { x: B.left - 0.19 * R, y: B.ny }, { x: B.left - 0.05 * R, y: B.ny }]);

    // slot C: from the ring down and right, then level into the number
    var cAng = wa > 0 ? clamp(C_ATTACH, t0 + 3, Math.max(t0 + 3, t1 - 3)) : (t0 + t1) / 2;
    leader([pt(cAng, out), { x: C.left - 0.19 * R, y: C.ny + 0.01 * R }, { x: C.left - 0.075 * R, y: C.ny + 0.02 * R }]);

    // 5. the middle
    cnum.style.top = Math.round(cy - 0.06 * R - cnum.offsetHeight / 2) + 'px';
    cnum.style.left = Math.round(cx - cnum.offsetWidth / 2) + 'px';
    clab.style.top = Math.round(cy + 0.40 * R - clab.offsetHeight / 2) + 'px';
    clab.style.left = Math.round(cx - clab.offsetWidth / 2) + 'px';
  }

  function init() {
    var roots = [].slice.call(document.querySelectorAll('.pp-donut'));
    roots.forEach(function (root) {
      layout(root);
      if (window.ResizeObserver) {
        var last = 0;
        new ResizeObserver(function () {
          var w = root.clientWidth;
          if (w === last) return;
          last = w;
          layout(root);
        }).observe(root);
      } else {
        window.addEventListener('resize', function () { layout(root); });
      }
    });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { roots.forEach(layout); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
}());
